import { DemandeCounters } from '../../../models/demande-counters.model';
import { DemandeAffiliation } from '@lib/models/demande-affiliation/demande-affiliation.model';
import { DemandeAffiliationStatutConsultant } from '@lib/models/demande-affiliation/types/demande-statut.type';
import { Pagination } from '@lib/models/pagination.model';
import { combineLatest, lastValueFrom, map, Observable } from 'rxjs';
import { DemandeAffiliationConsultantService } from '../demande-affiliation-consultant.service';
import { inject, Injectable } from '@angular/core';
import {
  DeleteDemandeAffiliationGQL,
  DemandeAffiliationStatutConsultantEnum,
  GetDemandeAffiliationsCountByStatutConsultantGQL,
  GetDemandeAffiliationsFilteredConsultantGQL,
} from '@graphql/generated';
import { StatutConsultantMapper } from '@lib/mappers/graphql/statut-consultant-mapper';

@Injectable({ providedIn: 'root' })
export class GraphqlDemandeAffiliationConsultantService extends DemandeAffiliationConsultantService {
  private getDemandeAffiliationCountGql = inject(
    GetDemandeAffiliationsCountByStatutConsultantGQL
  );
  private getDemandesAffiliationGql = inject(GetDemandeAffiliationsFilteredConsultantGQL);
  private deleteDemandeAffiliationGql = inject(DeleteDemandeAffiliationGQL);

  private readonly aTraiterStatutsGql: DemandeAffiliationStatutConsultantEnum[] =
    this.aTraiterStatuts.map(s => StatutConsultantMapper.toGraphQl(s)!);

  private readonly enCoursStatutsGql: DemandeAffiliationStatutConsultantEnum[] =
    this.enCoursStatuts.map(s => StatutConsultantMapper.toGraphQl(s)!);

  private queryRefATraiter = this.getDemandeAffiliationCountGql.watch({
    variables: { statuts: this.aTraiterStatutsGql },
    ...{ fetchPolicy: 'network-only' },
  });

  private queryRefEnCours = this.getDemandeAffiliationCountGql.watch({
    variables: { statuts: this.enCoursStatutsGql },
    ...{ fetchPolicy: 'network-only' },
  });

  private aTraiter$ = this.queryRefATraiter.valueChanges.pipe(
    map(result => result.data?.demandeAffiliationCountByStatutConsultant)
  );

  private enCours$ = this.queryRefEnCours.valueChanges.pipe(
    map(result => result.data?.demandeAffiliationCountByStatutConsultant)
  );

  public override watchDemandesCountByCategories(): Observable<DemandeCounters> {
    const interval = 15000;

    this.queryRefEnCours.startPolling(interval);
    this.queryRefATraiter.startPolling(interval);

    return combineLatest([this.aTraiter$, this.enCours$]).pipe(
      map(
        ([aTraiter, enCours]) =>
          ({
            enCoursDeTraitement: enCours,
            aTraiter: aTraiter,
          }) as DemandeCounters
      )
    );
  }

  private updateCountByCategories(): void {
    this.queryRefATraiter.refetch();
    this.queryRefEnCours.refetch();
  }

  public override async getDemandeAffiliations(
    search: string | null,
    statuts: DemandeAffiliationStatutConsultant[],
    pageIndex: number,
    pageSize: number
  ): Promise<Pagination<DemandeAffiliation>> {
    const statutsGql = statuts.map(s => StatutConsultantMapper.toGraphQl(s)!);
    const queryResult = await lastValueFrom(
      this.getDemandesAffiliationGql.fetch({
        variables: {
          take: pageSize,
          skip: pageSize * pageIndex,
          search: search,
          statuts: statutsGql,
        },
        ...{ fetchPolicy: 'network-only' },
      })
    );

    const data = queryResult.data!.demandeAffiliationFilteredConsultant;

    const demandesAffiliaiton: DemandeAffiliation[] =
      data?.items?.map(
        i =>
          ({
            id: i.id,
            publicId: i.publicId,
            nomUsage: i.nomUsage,
            prenom: i.prenom,
            mobile: i.mobile,
            email: i.email,
            statutConsultant: StatutConsultantMapper.fromGraphQl(i.statutConsultant),
            dateUpdate: i.dateUpdate,
            dateDeclaration: i.dateDeclaration,
          }) as DemandeAffiliation
      ) ?? [];

    return new Pagination<DemandeAffiliation>(
      demandesAffiliaiton,
      pageIndex,
      pageSize,
      data!.totalCount
    );
  }

  public override async deleteDemandeAffiliation(publicId: string): Promise<void> {
    await lastValueFrom(
      this.deleteDemandeAffiliationGql.mutate({ variables: { publicId: publicId } })
    );
    this.updateCountByCategories();
  }
}
